import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DataTable from "react-data-table-component";

import api from "../../services/api";
import { setAllCohorts, setMenuCohorts } from "../../features/cohort/cohortSlice";
import RunDetailsModal from "./RunDetailsModal";
import CampaignPreviewModal from "./CampaignPreviewModal";
import "../../styles/materialize.css";

const normalizeStatus = value => {
  if (!value) return "";
  return String(value).toLowerCase();
};

const isPreviewReadyStatus = status =>
  status === "completed" || status === "xml_generated";


export default function MaterializeCohort() {
  const dispatch = useDispatch();
  const allCohorts = useSelector(state => state.cohort.all);
  const menuCohorts = useSelector(state => state.cohort.menu);


  const [search, setSearch] = useState("");
  const [loadingId, setLoadingId] = useState(null);

  const [openSegment, setOpenSegment] = useState(null);
  const [runData, setRunData] = useState(null);

  const [campaignPreview, setCampaignPreview] = useState(null);

  /**
   * previewJobs structure:
   * {
   *   [segment_id]: {
   *     status: "idle" | "generating" | "running" | "completed" | "failed",
   *     job_id?: string,
   *     payload?: object,
   *     prompt_text?: string
   *   }
   * }
   */
  const [previewJobs, setPreviewJobs] = useState({});
  const pollersRef = useRef({});
  const allCohortsRef = useRef(allCohorts);
  const menuCohortsRef = useRef(menuCohorts);

  useEffect(() => {
    allCohortsRef.current = allCohorts;
  }, [allCohorts]);

  useEffect(() => {
    menuCohortsRef.current = menuCohorts;
  }, [menuCohorts]);

  const updateCohortBySegment = (segmentId, updates) => {
    const updated = allCohortsRef.current.map(item =>
      item.segment_id === segmentId ? { ...item, ...updates } : item
    );
    dispatch(setAllCohorts(updated));
  };

  const updateStoredCohorts = (row, updates) => {
    const matchesRow = item =>
      item.segment_id === row.segment_id && item.trace_id === row.trace_id;

    const updatedAll = allCohortsRef.current.map(item =>
      matchesRow(item) ? { ...item, ...updates } : item
    );
    dispatch(setAllCohorts(updatedAll));

    const updatedMenu = menuCohortsRef.current.map(item =>
      matchesRow(item) ? { ...item, ...updates } : item
    );
    dispatch(setMenuCohorts(updatedMenu));
  };

  const toPreviewPayloadFromRow = row => ({
    status: "completed",
    workflow_name: row.workflow_name,
    workflow_xml: row.workflow_xml
  });

  /* ---------- CLEANUP ALL POLLERS ON UNMOUNT ---------- */
  useEffect(() => {
    return () => {
      Object.values(pollersRef.current).forEach(clearInterval);
      pollersRef.current = {};
    };
  }, []);

  const startPolling = (segmentId, jobId, promptText) => {
    if (!jobId || pollersRef.current[segmentId]) return;

    const poller = setInterval(async () => {
      try {
        const statusRes = await api.get(
          `https://aidemo.fusionsystemsinc.com/api/campaigns/preview/new/xml/${jobId}`,
          { headers: { accept: "application/json" } }
        );
        const status = normalizeStatus(statusRes.data?.status);

        if (status === "completed") {
          clearInterval(pollersRef.current[segmentId]);
          delete pollersRef.current[segmentId];

          setPreviewJobs(prev => ({
            ...prev,
            [segmentId]: {
              status: "completed",
              job_id: jobId,
              payload: statusRes.data,
              prompt_text: promptText
            }
          }));

          updateCohortBySegment(segmentId, {
            status: "XML GENERATED",
            job_status: "XML GENERATED",
            job_id: jobId,
            workflow_name: statusRes.data.workflow_name || null,
            workflow_xml: statusRes.data.workflow_xml || null
          });
        }

        if (status === "failed") {
          clearInterval(pollersRef.current[segmentId]);
          delete pollersRef.current[segmentId];

          setPreviewJobs(prev => ({
            ...prev,
            [segmentId]: { status: "failed", job_id: jobId }
          }));

          updateCohortBySegment(segmentId, {
            job_status: "failed",
            job_id: jobId
          });
        }
      } catch (e) {
        console.error("Polling error", e);
      }
    }, 5000);

    pollersRef.current[segmentId] = poller;
  };

  useEffect(() => {
    allCohorts.forEach(row => {
      const status = normalizeStatus(row.job_status);
      if (!status) return;

      if (isPreviewReadyStatus(status) && row.workflow_xml) {
        setPreviewJobs(prev => ({
          ...prev,
          [row.segment_id]: {
            status: "completed",
            job_id: row.job_id,
            payload: toPreviewPayloadFromRow(row),
            prompt_text: row.prompt_text
          }
        }));
      }

      if (status === "running" && row.job_id) {
        setPreviewJobs(prev => ({
          ...prev,
          [row.segment_id]: {
            status: "running",
            job_id: row.job_id,
            prompt_text: row.prompt_text
          }
        }));

        startPolling(row.segment_id, row.job_id, row.prompt_text);
      }
    });
  }, [allCohorts]);

  /* ---------- GROUP LATEST BY trace_id ---------- */
  // const groupedLatest = {};
  // allCohorts.forEach(item => {
  //   if (
  //     !groupedLatest[item.trace_id] ||
  //     new Date(item.created_at) >
  //       new Date(groupedLatest[item.trace_id].created_at)
  //   ) {
  //     groupedLatest[item.trace_id] = item;
  //   }
  // });
  const groupedLatest = allCohorts.reduce((acc, item) => {
    const existing = acc[item.trace_id];
    if (
      !existing ||
      new Date(item.created_at) > new Date(existing.created_at)
    ) {
      acc[item.trace_id] = item;
    }

    return acc;
  }, {});


  const materializable = Object.values(groupedLatest)
    .filter(c =>
      ["PREVIEWED", "APPROVED", "FAILED", "COMPLETED", "UNKNOWN", "XML GENERATED"].includes(c.status)
    )
    .sort((a, b) => b.created_at.localeCompare(a.created_at));

  const filtered = materializable.filter(c =>
    c.cohort_name.toLowerCase().includes(search.toLowerCase())
  );


  const viewRunDetails = async cohort => {
    if (!cohort.run_id) {
      alert("Run details are not available for this cohort.");
      return;
    }

    try {
      setLoadingId(cohort.segment_id);

      const runRes = await api.get(
        `https://aidemo.fusionsystemsinc.com/api/v1/admin/runs/${cohort.run_id}`,
        { headers: { accept: "application/json" } }
      );

      updateStoredCohorts(cohort, {
        run_result: runRes.data,
        materialize_completed: true
      });

      setRunData({
        run_id: cohort.run_id,
        prompt_text: cohort.prompt_text
      });
      setOpenSegment(cohort.segment_id);
    } catch (err) {
      console.error("Fetch run details failed", err);
      alert("Failed to load run details");
    } finally {
      setLoadingId(null);
    }
  };

  /* ---------- MATERIALIZE ---------- */
  const materialize = async (cohort) => {
    try {
      setLoadingId(cohort.segment_id);

      if (cohort.materialize_completed) {
        await viewRunDetails(cohort);
        return;
      }

      const res = await api.post(
        `https://aidemo.fusionsystemsinc.com/api/v1/admin/cohorts/${cohort.segment_id}/materialize`,
        {},
        { headers: { accept: "application/json" } }
      );

      const runId = res.data?.run_id;
      if (!runId) {
        updateStoredCohorts(cohort, {
          status: "FAILED",
          materialize_completed: false
        });
        return;
      }

      const runRes = await api.get(
        `https://aidemo.fusionsystemsinc.com/api/v1/admin/runs/${runId}`,
        { headers: { accept: "application/json" } }
      );

      const finalStatus =
        runRes.data.status === "SUCCEEDED" ||
        runRes.data.status === "COMPLETED"
          ? "COMPLETED"
          : "FAILED";

      updateStoredCohorts(cohort, {
        status: finalStatus,
        run_id: runId,
        run_result: runRes.data,
        materialize_completed: finalStatus === "COMPLETED"
      });
    } catch (err) {
      console.error("Materialize failed", err);
    } finally {
      setLoadingId(null);
    }
  };

  /* ---------- START + POLL ALTERYX PREVIEW JOB ---------- */
  const previewCampaign = async row => {
    const segment_id = row.segment_id;
    const prompt_text = row.prompt_text;
    const persistedStatus = normalizeStatus(row.job_status);

    if (isPreviewReadyStatus(persistedStatus) && row.workflow_xml) {
      const preview = {
        status: "completed",
        job_id: row.job_id,
        payload: toPreviewPayloadFromRow(row),
        prompt_text
      };
      setPreviewJobs(prev => ({ ...prev, [segment_id]: preview }));
      setCampaignPreview(preview);
      return;
    }

    if (previewJobs[segment_id]?.status === "completed") {
      setCampaignPreview(previewJobs[segment_id]);
      return;
    }

    try {
      setPreviewJobs(prev => ({
        ...prev,
        [segment_id]: { status: "generating", prompt_text }
      }));
      updateCohortBySegment(segment_id, { job_status: "generating" });

      const startRes = await api.post(
        "https://aidemo.fusionsystemsinc.com/api/campaigns/preview/new/xml",
        null,
        { params: { segment_id }, headers: { accept: "application/json" } }
      );

      const jobId = startRes.data.job_id;

      setPreviewJobs(prev => ({
        ...prev,
        [segment_id]: { status: "running", job_id: jobId, prompt_text }
      }));
      updateCohortBySegment(segment_id, {
        job_status: "running",
        job_id: jobId
      });

      startPolling(segment_id, jobId, prompt_text);
    } catch (err) {
      console.error("Preview job start failed", err);
      setPreviewJobs(prev => ({
        ...prev,
        [segment_id]: { status: "failed" }
      }));
      updateCohortBySegment(segment_id, { job_status: "failed" });
    }
  };
  /* ---------- Deletion ----------- */
  const deleteCohort = async (row) => {
    if (!window.confirm("Are you sure you want to delete this cohort?")) return;

    try {
      await api.post(
        `https://aidemo.fusionsystemsinc.com/api/v1/admin/traces/${row.trace_id}/delete`,
        null,
        { headers: { accept: "application/json" } }
      );

      // 1️⃣ remove from ALL cohorts
      const updatedAll = allCohortsRef.current.filter(
        c => c.trace_id !== row.trace_id
      );

      dispatch(setAllCohorts(updatedAll));

      // 2️⃣ remove from MENU cohorts
      const updatedMenu = menuCohorts.filter(
        c => c.trace_id !== row.trace_id
      );

      dispatch(setMenuCohorts(updatedMenu));

    } catch (err) {
      console.error("Delete failed", err);
      alert("Failed to delete cohort");
    }
  };


  /* ---------- TABLE COLUMNS ---------- */
  const columns = [
    {
      name: "Cohort Name",
      selector: row => row.cohort_name,
      sortable: true,
      grow: 2,          // main column
      wrap: true,
      center: true
    },
    {
      name: "Date",      
      selector: row => {
        const d = new Date(row.created_at);
        return `${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}/${d.getFullYear()}`;
      },
      sortable: true,
      grow: 1,
      wrap: true,
      center: true
    },
    {
      name: "Email",
      selector: row => row.created_by,
      grow: 2,
      wrap: true,
      center: true
    },
    {
      name: "Quantity",
      selector: row => row.preview_count?.preview_count ? row.preview_count?.preview_count : row?.preview_count,
      grow: 1,
      center: true
    },
    {
      name: "Status",
      selector: row => row.status,
      grow: 1,
      center: true,
      cell: row => (
        <span className={`admin-status ${row.status.toLowerCase().replace(/\s+/g, "_")}`}>
          {row.status}
        </span>
      )
    },
    {
      name: "Action",
      grow: 2,
      center: true,
      cell: row => {
        const state =
          previewJobs[row.segment_id]?.status ||
          normalizeStatus(row.job_status);
        const isPreviewReady = isPreviewReadyStatus(state);

        return (
          <div className="admin-actions">
            <button
              className="admin-btn preview"
              disabled={state === "generating" || state === "running"}
              onClick={() =>
                previewCampaign(row)
              }
            >
              {state === "generating" && "Generating..."}
              {state === "running" && "Running..."}
              {isPreviewReady && "View Preview"}
              {!state && "Alteryx Spec Preview"}
            </button>

            {row.materialize_completed ? (
              <button
                className="admin-btn success"
                disabled={loadingId === row.segment_id}
                onClick={() => viewRunDetails(row)}
              >
                {loadingId === row.segment_id ? "Loading..." : "View Details"}
              </button>
            ) : (
              <button
                className="admin-btn primary"
                disabled={loadingId === row.segment_id}
                onClick={() => materialize(row)}
              >
                {loadingId === row.segment_id
                  ? "Processing..."
                  : "Materialize"}
              </button>
            )}
            {/* DELETE */}
            <button
              className="admin-btn danger icon"
              title="Delete cohort"
              onClick={() => deleteCohort(row)}
            >
              <i className="fa-solid fa-trash"></i>
            </button>
          </div>
        );
      }
    }
  ];

  return (
    <div className="admin-page">
      <h2>Materialize Cohort</h2>

      <DataTable
        columns={columns}
        data={filtered}
        pagination
        fixedHeader
        fixedHeaderScrollHeight="65vh"
        highlightOnHover
        responsive
        subHeader
        subHeaderComponent={
          <input
            type="text"
            placeholder="Search cohort..."
            className="datatable-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        }
      />

      {openSegment && (
        <RunDetailsModal
          open={true}
          onClose={() => setOpenSegment(null)}
          runData={runData}
          runResult={
            allCohorts.find(c => c.segment_id === openSegment)
              ?.run_result
          }
        />
      )}

      {campaignPreview && (
        <CampaignPreviewModal
          open={true}
          onClose={() => setCampaignPreview(null)}
          data={campaignPreview.payload}
          prompt_text={campaignPreview.prompt_text}
        />
      )}
    </div>
  );
}



